import { Injectable } from '@angular/core';
import {
  AndOrOperator,
  Outcome,
  OutcomeAnswer,
  OutcomeOperator,
  Widget,
  ButtonActionType,
  WidgetComponentType,
  WidgetComponentSubType,
} from '../../widgets.model';
import { Logger, LoggerFactory } from '../../../core/logger.service';
import WidgetComponentHelpers from '../../widget-component-helpers';

export interface IFormSubmitResult {
  shouldAbort?: boolean;
  action?: ButtonActionType;
  stepId?: number;
}

const FormSubmitAction = {
  SwitchStep: ButtonActionType.SwitchState,
};

@Injectable({
  providedIn: 'root',
})
export class OutcomeLogicTesterService {
  private readonly logger: Logger;

  constructor(private loggerFactory: LoggerFactory) {
    this.logger = this.loggerFactory.get('OutcomeLogicTesterService');
  }

  public evaluate(outcomes: Outcome[], inputs: Record<string, any>): Outcome | null {
    const widget: Widget = {
      layout: null as any,
      outcomes: outcomes,
    } as any;

    const result: IFormSubmitResult = {};
    const processedResult = this.processOutcomes(widget, inputs, result);

    if (processedResult.action === ButtonActionType.SwitchState && processedResult.stepId) {
      return outcomes.find((o) => o.stateId === processedResult.stepId) || null;
    }
    return null;
  }

  public processOutcomes(
    widget: Widget,
    customData: { [key: string]: any },
    result: IFormSubmitResult,
  ): IFormSubmitResult {
    if (widget.outcomes?.length > 0) {
      let matchedOutcome: Outcome = null;

      // First, try to find a matching non-default outcome
      const nonDefaultOutcomes = widget.outcomes.filter((o) => !o.isDefault);
      this.logger.debug(`Checking ${nonDefaultOutcomes.length} non-default outcomes`);

      for (const outcome of nonDefaultOutcomes) {
        if (this.isOutcomeMatching(outcome, customData)) {
          matchedOutcome = outcome;
          this.logger.info(`Matched non-default outcome (stateId=${outcome.stateId})`);
          break;
        }
      }

      // If no non-default outcome matches, use the default outcome
      if (!matchedOutcome) {
        matchedOutcome = widget.outcomes.find((o) => o.isDefault);
        if (matchedOutcome) {
          this.logger.info(`Falling back to default outcome (stateId=${matchedOutcome.stateId})`);
        } else {
          this.logger.warn('No matching outcome found and no default outcome available');
        }
      }

      if (!result) {
        result = { shouldAbort: false };
        this.logger.debug('Created new result object with shouldAbort=false');
      }

      if (matchedOutcome && matchedOutcome.stateId) {
        result.action = FormSubmitAction.SwitchStep;
        result.stepId = matchedOutcome.stateId;
        this.logger.debug(`Updated result to SwitchStep (stepId=${result.stepId})`);
      }
    } else {
      this.logger.debug('No outcomes found');
    }

    return result;
  }

  private isOutcomeMatching(outcome: Outcome, data: { [key: string]: any }): boolean {
    this.logger.debug(`Evaluating outcome (answers=${outcome.answers?.length})`);

    if (!outcome.answers || outcome.answers.length === 0) {
      return false;
    }

    let result = this.evaluateAnswer(outcome.answers[0], data);
    this.logger.debug(`Answer 0 result=${result}`);

    for (let i = 1; i < outcome.answers.length; i++) {
      const answer = outcome.answers[i];
      const prevAnswer = outcome.answers[i - 1];
      const andOrOperator = prevAnswer.andOrOperator;

      // Short-circuit evaluation
      if (andOrOperator === AndOrOperator.And && !result) {
        // Check if there’s any OR later that could still flip the result to true
        const hasFutureOr = outcome.answers.slice(i).some((a) => a.andOrOperator === AndOrOperator.Or);
        if (!hasFutureOr) {
          this.logger.debug(`Short-circuit: AND chain is false and no future OR, skipping remaining answers`);
          break;
        }
      }
      if (andOrOperator === AndOrOperator.Or && result) {
        this.logger.debug(`Short-circuit: OR chain is true, skipping remaining answers`);
        break;
      }

      const answerResult = this.evaluateAnswer(answer, data);
      this.logger.debug(`Answer ${i} result=${answerResult}, andOrOperator=${andOrOperator}`);

      if (andOrOperator === AndOrOperator.Or) {
        result = result || answerResult;
      } else {
        result = result && answerResult;
      }
    }

    this.logger.debug(`Outcome match result=${result}`);
    return result;
  }

  private evaluateAnswer(answer: OutcomeAnswer, data: { [key: string]: any }): boolean {
    const formValue = data[answer.question];
    this.logger.debug(
      `Checking question="${answer.question}", operator=${answer.operator}, expected="${answer.value}", actual="${formValue}"`,
    );

    if (formValue === undefined || formValue === null) {
      this.logger.debug(`Answer not found for question="${answer.question}"`);
      return false;
    }

    switch (answer.operator) {
      case OutcomeOperator.Equals:
        return this.checkEquals(formValue, answer.value);
      case OutcomeOperator.NotEquals:
        return !this.checkEquals(formValue, answer.value);
      case OutcomeOperator.LessThan:
        const numericFormValue1 = parseFloat(formValue);
        const numericAnswerValue1 = parseFloat(answer.value as string);
        return !isNaN(numericFormValue1) && !isNaN(numericAnswerValue1) && numericFormValue1 < numericAnswerValue1;
      case OutcomeOperator.GreaterThan:
        const numericFormValue2 = parseFloat(formValue);
        const numericAnswerValue2 = parseFloat(answer.value as string);
        return !isNaN(numericFormValue2) && !isNaN(numericAnswerValue2) && numericFormValue2 > numericAnswerValue2;
      case OutcomeOperator.LessThanOrEqual:
        const numericFormValue3 = parseFloat(formValue);
        const numericAnswerValue3 = parseFloat(answer.value as string);
        return !isNaN(numericFormValue3) && !isNaN(numericAnswerValue3) && numericFormValue3 <= numericAnswerValue3;
      case OutcomeOperator.GreaterThanOrEqual:
        const numericFormValue4 = parseFloat(formValue);
        const numericAnswerValue4 = parseFloat(answer.value as string);
        return !isNaN(numericFormValue4) && !isNaN(numericAnswerValue4) && numericFormValue4 >= numericAnswerValue4;
      default:
        this.logger.warn(`Unsupported operator: ${answer.operator}`);
        return false;
    }
  }

  private checkEquals(formValue: any, answerValue: string | string[]): boolean {
    let targetValues: string[] = [];

    if (Array.isArray(answerValue)) {
      targetValues = answerValue.map(String);
    } else {
      try {
        const parsed = JSON.parse(answerValue);
        if (Array.isArray(parsed)) {
          targetValues = parsed.map(String);
        } else {
          targetValues = [String(answerValue)];
        }
      } catch {
        targetValues = [String(answerValue)];
      }
    }

    if (Array.isArray(formValue)) {
      // If any value in input matches any value in target
      return formValue.some((val) => targetValues.includes(String(val)));
    }

    return targetValues.includes(String(formValue));
  }

  public calculateScoreRange(
    widget: Widget,
    scoreInputName: string | null,
  ): { min: number; max: number; hasScore: boolean } {
    const allComponents = WidgetComponentHelpers.getAllComponentsOnWidget(widget);
    const scoreComponents = allComponents.filter((c) =>
      c.listItems?.some((item) => item.score !== undefined && item.score !== 0),
    );

    const hasScore = !!scoreInputName && scoreComponents.length > 0;
    let minScore = 0;
    let maxScore = 0;

    if (!hasScore) {
      return { min: 0, max: 0, hasScore: false };
    }

    scoreComponents.forEach((component) => {
      const scores = component.listItems.map((item) => item.score || 0);

      // Check for multi-select
      let isMultiSelect = false;
      if (component.type === WidgetComponentType.ButtonList) {
        if (component.subType === WidgetComponentSubType.ButtonListCheckbox) {
          isMultiSelect = true;
        } else if (component.subType === WidgetComponentSubType.ButtonListRadio) {
          isMultiSelect = false;
        } else {
          isMultiSelect = component.buttonListProperties?.allowMultipleSelection || false;
        }
      }

      if (isMultiSelect) {
        const positiveScores = scores.filter((s) => s > 0);
        const negativeScores = scores.filter((s) => s < 0);

        maxScore += positiveScores.reduce((a, b) => a + b, 0);
        minScore += negativeScores.reduce((a, b) => a + b, 0);
      } else {
        if (scores.length > 0) {
          maxScore += Math.max(...scores);
          minScore += Math.min(...scores);
        }
      }
    });

    return { min: minScore, max: maxScore, hasScore };
  }

  public calculateMissingScores(
    outcomes: Outcome[],
    scoreInputName: string,
    minScore: number,
    maxScore: number,
  ): { missingScoresRanges: string; missingScoresIsDefault: boolean; missingScores: number[] } {
    if (!scoreInputName) {
      return { missingScoresRanges: '', missingScoresIsDefault: false, missingScores: [] };
    }

    const missingScores: number[] = [];
    // Limit range to avoid performance issues if something is wrong
    const start = Math.max(minScore, -10000);
    const end = Math.min(maxScore, 10000);

    for (let s = start; s <= end; s++) {
      if (!this.isScoreCovered(outcomes, s, scoreInputName)) {
        missingScores.push(s);
      }
    }

    const ranges = this.formatMissingScores(missingScores);
    const hasDefaultOutcome = outcomes.some((outcome) => outcome.isDefault);

    if (ranges) {
      return {
        missingScoresRanges: ranges,
        missingScoresIsDefault: hasDefaultOutcome,
        missingScores,
      };
    } else {
      return { missingScoresRanges: '', missingScoresIsDefault: false, missingScores: [] };
    }
  }

  private isScoreCovered(outcomes: Outcome[], score: number, scoreInputName: string): boolean {
    // Check if any non-default outcome covers this score
    return outcomes.filter((o) => !o.isDefault).some((o) => this.outcomeCoversScore(o, score, scoreInputName));
  }

  private outcomeCoversScore(outcome: Outcome, score: number, scoreInputName: string): boolean {
    if (!outcome.answers || outcome.answers.length === 0) {
      return true; // No conditions = covers everything
    }

    let result = this.evaluateAnswerForScore(outcome.answers[0], score, scoreInputName);

    for (let i = 1; i < outcome.answers.length; i++) {
      const answer = outcome.answers[i];
      const prevAnswer = outcome.answers[i - 1];
      const andOrOperator = prevAnswer.andOrOperator;

      const answerResult = this.evaluateAnswerForScore(answer, score, scoreInputName);

      if (andOrOperator === AndOrOperator.Or) {
        result = result || answerResult;
      } else {
        result = result && answerResult;
      }
    }

    return result;
  }

  private evaluateAnswerForScore(answer: OutcomeAnswer, score: number, scoreInputName: string): boolean {
    // If the question is NOT the score input, we assume it CAN match (return true)
    // because we are looking for scores that are IMPOSSIBLE to match.
    if (answer.question !== scoreInputName) {
      return true;
    }

    // If it IS the score input, evaluate the condition
    const formValue = score;

    switch (answer.operator) {
      case OutcomeOperator.Equals:
        if (Array.isArray(answer.value)) {
          return answer.value.some((v) => parseFloat(v) === formValue);
        }
        return parseFloat(answer.value as string) === formValue;
      case OutcomeOperator.NotEquals:
        if (Array.isArray(answer.value)) {
          return !answer.value.some((v) => parseFloat(v) === formValue);
        }
        return parseFloat(answer.value as string) !== formValue;
      case OutcomeOperator.LessThan:
        return formValue < parseFloat(answer.value as string);
      case OutcomeOperator.GreaterThan:
        return formValue > parseFloat(answer.value as string);
      case OutcomeOperator.LessThanOrEqual:
        return formValue <= parseFloat(answer.value as string);
      case OutcomeOperator.GreaterThanOrEqual:
        return formValue >= parseFloat(answer.value as string);
      default:
        return false;
    }
  }

  public getRanges(scores: number[]): { start: number; end: number }[] {
    if (scores.length === 0) return [];
    const ranges: { start: number; end: number }[] = [];
    let start = scores[0];
    let prev = scores[0];

    for (let i = 1; i < scores.length; i++) {
      const current = scores[i];
      if (current !== prev + 1) {
        ranges.push({ start, end: prev });
        start = current;
      }
      prev = current;
    }
    ranges.push({ start, end: prev });
    return ranges;
  }

  public generateOutcomesFromRanges(ranges: { start: number; end: number }[]): Outcome[] {
    const generatedOutcomes: Outcome[] = [];
    ranges.forEach((range) => {
      const outcome: Outcome = {
        stateId: null,
        isDefault: false,
        answers: [],
        customDataUpdate: { key: '', value: '' },
      };

      if (range.start === range.end) {
        outcome.answers.push({
          question: 'score',
          operator: OutcomeOperator.Equals,
          value: range.start.toString(),
          andOrOperator: AndOrOperator.And,
        });
      } else {
        outcome.answers.push({
          question: 'score',
          operator: OutcomeOperator.GreaterThanOrEqual,
          value: range.start.toString(),
          andOrOperator: AndOrOperator.And,
        });
        outcome.answers.push({
          question: 'score',
          operator: OutcomeOperator.LessThanOrEqual,
          value: range.end.toString(),
          andOrOperator: AndOrOperator.And,
        });
      }
      generatedOutcomes.push(outcome);
    });
    return generatedOutcomes;
  }

  private formatMissingScores(scores: number[]): string {
    const ranges = this.getRanges(scores);
    return ranges.map((r) => this.formatRange(r.start, r.end)).join(', ');
  }

  private formatRange(start: number, end: number): string {
    if (start === end) {
      return `${start}`;
    }
    if (end === start + 1) {
      return `${start}, ${end}`;
    }
    return `${start}-${end}`;
  }
}
